import { Controller, FormProvider, useForm } from "react-hook-form";
import { SettingsPropertiesWrapper } from "./styles";
import Input from "@Common/input";
import { useTranslation } from "react-i18next";
import { APIS_KEYS, usePutAutomationWheelhouseProperties } from "@services";
import { Dispatch, SetStateAction, useContext, useEffect } from "react";
import { Checkbox } from "@mui/material";
import AirbnbIcon from "@assets/svg/airbnb.svg?react";
import GathernIcon from "@assets/svg/GathernIcon2.svg?react";
import BookingIcon from "@assets/svg/BookingIcon.svg?react";

import { Channals } from "../../styles";
import CustomButton from "@Common/CustomButton";
import { ToastMessageContext } from "@context/toast-message-cotnext";
import { LoadingWrapper } from "@components";
import { IResponseGetWheelhouseProperties } from "src/services/calender/getWheelhouseProperties/types";
import {
  QueryObserverResult,
  RefetchOptions,
  useQueryClient,
} from "@tanstack/react-query";

/* ---------------------------------- Types --------------------------------- */

interface IForm {
  searchKey: string;
  properties: string[];
}

interface IChannals {
  1: JSX.Element;
  2: JSX.Element;
  3: JSX.Element;
}

const channals: IChannals = {
  1: <AirbnbIcon />,
  2: <GathernIcon />,
  3: <BookingIcon />,
};

interface SettingsPropertiesProps {
  dataWheelhouseProperties: IResponseGetWheelhouseProperties | undefined;
  setSearchKey: (value: string) => void;
  setIsSettingsPropertiesOpen: Dispatch<SetStateAction<boolean>>;
  setIsPopUpEnabeled: Dispatch<
    SetStateAction<{
      disable: boolean;
      apply: boolean;
    }>
  >;
  refetch: any;
  setIsLoaderOpen: Dispatch<SetStateAction<boolean>>;
  isLoaderOpen: boolean;
}

/* ------------------------------- Component ------------------------------- */

export default function SettingsProperties({
  setSearchKey,
  setIsSettingsPropertiesOpen,
  setIsPopUpEnabeled,
  dataWheelhouseProperties,
  refetch,
  setIsLoaderOpen,
  isLoaderOpen,
}: SettingsPropertiesProps) {
  /* ---------------------------------- Hooks --------------------------------- */

  const { t } = useTranslation();

  const defaultChecked =
    dataWheelhouseProperties?.result
      .filter((item) => item.isActive)
      .map((item) => String(item.propertyId)) || [];

  const methods = useForm<IForm>({
    mode: "onChange",
    defaultValues: {
      searchKey: "",
      properties: defaultChecked,
    },
  });

  const { watch, setValue } = methods;
  const searchKeyWatch = watch("searchKey");
  const { showSuccess } = useContext(ToastMessageContext);
  const queryClient = useQueryClient();

  /* ---------------------------------- API'S --------------------------------- */

  const { mutateAsync } = usePutAutomationWheelhouseProperties();

  /* ----------------------------- Form Submission ----------------------------- */

  const onSubmit = async (data: IForm) => {
    setIsLoaderOpen(true);

    const selectedIds = data.properties;

    const payload =
      dataWheelhouseProperties?.result.map((item) => ({
        listingId: String(item.propertyId),
        enabled: selectedIds.includes(String(item.propertyId)),
      })) || [];

    const res = await mutateAsync(payload);
    if (res.status === 200) {
      setIsLoaderOpen(false);
      setIsSettingsPropertiesOpen(false);
      showSuccess(t("WebsiteBuilder.SettingsModified"));
      setIsPopUpEnabeled({
        apply: false,
        disable: false,
      });
      queryClient.invalidateQueries({ queryKey: [APIS_KEYS.GETCALENDERDATA] });
    }
    refetch();
    setIsPopUpEnabeled({
      apply: false,
      disable: false,
    });
    setIsLoaderOpen(false);
  };

  /* ------------------------------- LifeCycle ------------------------------- */

  useEffect(() => {
    setSearchKey(searchKeyWatch);
  }, [searchKeyWatch]);
  return (
    <SettingsPropertiesWrapper>
      <FormProvider {...methods}>
        <div className="containerSearchInput">
          <Input
            name="searchKey"
            placeholder={t("CATEGORYMANAGER.SEARCH")}
            type="search"
          />
        </div>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          id="wheelhousesettingform"
        >
          <LoadingWrapper loading={isLoaderOpen}>
            <div className="displayRes">
              {dataWheelhouseProperties?.result.map((item) => {
                const propertyIdStr = String(item.propertyId);
                const selected = watch("properties") || [];
                const isChecked = selected.includes(propertyIdStr);

                return (
                  <div className="continerRecord" key={propertyIdStr}>
                    <div className="containerInfo">
                      <span>{item.propertyName}</span>
                      <div className="containerChannels">
                        {item.channels.map((subItem, index) => (
                          <Channals key={index}>
                            <span>{channals[subItem.type as 1 | 2 | 3]}</span>
                          </Channals>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Controller
                        name="properties"
                        control={methods.control}
                        render={() => (
                          <Checkbox
                            checked={isChecked}
                            onChange={(e) => {
                              const updated = e.target.checked
                                ? [...selected, propertyIdStr]
                                : selected.filter((id) => id !== propertyIdStr);
                              setValue("properties", updated);
                            }}
                          />
                        )}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </LoadingWrapper>
          <div className="containerFooter">
            <CustomButton
              label={t("CALENDAR.close")}
              customBG="#fff"
              customColor="#000"
              customPadding="10px 24px"
              borderRadius="8px"
              type="button"
              onClick={() => {
                setIsSettingsPropertiesOpen(false);
              }}
            />
            <CustomButton
              label={t("CALENDAR.Apply Changes")}
              customBG="#5B3FFF"
              customColor="#fff"
              customPadding="10px 24px"
              borderRadius="8px"
              type="button"
              onClick={() => {
                setIsPopUpEnabeled({
                  apply: true,
                  disable: false,
                });
              }}
              disabled={dataWheelhouseProperties?.result.length === 0}
            />
          </div>
        </form>
      </FormProvider>
    </SettingsPropertiesWrapper>
  );
}
